<template>
  <div class="select-number-list__wrap">
    <div class="select-number-list__wrap--item" v-for="(item, index) in currentSelectedOptions" :key="item.id">
      <div class="select-number-list__wrap--item__left">
        <zm-tooltip :disabled="!item.inputTooltip" placement="top">
          <template #content>
            <div v-html="item.inputTooltip"></div>
          </template>
          <zm-select data-testid="checkout-select-195" :ref='`select-${item.id}`' @change='onSelectionChange' v-model="item.id" size="large" :filter-label="getFilterLabel"
                     :label="getSelectLabel(item)" :disabled="item.shouldDisabled ? item.shouldDisabled : item.disabled" popper-append-to-body
                     class="select-number-list__wrap--item__select" popper-class="select-number-list-dropdown"
                     :class="{'select-number-list__wrap--item__select--simple': hasNoDesc(getOptionList(item.id))}">
            <zm-option data-testid="checkout-option-196" v-for="option in getOptionList(item.id)" :key="option.id" :value="option.id"
                       class="select-number-list-dropdown__option">
              <div class="select-number-list-dropdown__title">{{ option.name }}</div>
              <div class="select-number-list-dropdown__desc">{{ option.desc }}</div>
            </zm-option>
            <span slot="prefix" class="">{{ getFilterObj(item.id).name }}</span>
          </zm-select>
        </zm-tooltip>
        <span v-if="label" class="select-number-list__label">{{ label }}</span>
        <zm-tooltip v-if="getFilterObj(item.id).tooltip" :placement="$context.is.modal ? 'left' : 'top'" tab-to-leave>
          <template #content>
            <div style="min-width: 296px;" v-html="getFilterObj(item.id).tooltip"></div>
          </template>
          <zm-button data-testid="checkout-button-197" type="icon" ghost icon="zm-icon-info-outline" class="select-number-list__wrap--item__tooltip"
                     :aria-label="getLearnMoreLabel(item)"/>
        </zm-tooltip>
      </div>
      <div class="select-number-list__wrap--item__right">
        <InputNumberWithPopover :data-tracking='dataTracking' v-model="item.number" size="large" :min="item.min"
                                controls-position="both"
                                :max="item.max" :label="`${item.name} ${item.desc} Select the number of plans`"
                                :disabled="item.disabled"
                                :decreaseTips="item.decreaseTips"
        />
        <div class="select-number-list__wrap--item__number">
          {{ unit }}
        </div>
        <zm-tooltip :disabled="!item.deleteTips" placement="top">
          <template #content>
            <div v-html="item.deleteTips"></div>
          </template>
          <div>
            <zm-button data-testid="checkout-button-198" v-if="deletable" type="icon" icon="zm-icon-close" class="select-number-list__wrap--item__close"
                       :disabled="item.shouldDisabled ? item.shouldDisabled : (item.disabled || item.disableDelete)" ghost size="large"
                       @click="removeItem(item)" :aria-label="getRemoveLabel(item)"></zm-button>
          </div>
        </zm-tooltip>
      </div>
      <div class="select-number-list__wrap--item__divider" v-if="index < currentSelectedOptions.length - 1"></div>
    </div>
  </div>
</template>
<script>
/**
 * Select Number List Component
 *
 * @props {{ id: string, name: string, desc: string, tooltip: string, min: number, max: number, disabled: boolean }} optionList - All select option list
 * @props {{ id: string, number: number }} selectedOptions - Selected option list
 * @props { boolean } deletable - Whether you can delete item
 * @props { function } beforeChange - Before change item
 * @props { string } title - Title of select number list
 * @method newItem - Add new item
 */
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';

export default {
  name: 'SelectNumberList',
  components: {InputNumberWithPopover},
  model: {
    prop: 'selectedOptions',
    event: 'change',
  },
  props: {
    dataTracking: {
      type: Object,
      required: false,
    },
    selectedOptions: {
      type: Array,
      default: () => [],
    },
    optionList: {
      type: Array,
      default: () => [],
    },
    deletable: {
      type: Boolean,
      default: true,
    },
    beforeChange: {
      type: Function,
      default: null,
    },
    title: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    unit: {
      type: String,
      default: function () {
        return this.$t('billing.planform.unit.number').toLowerCase()
      },
    }
  },
  data() {
    return {
      currentSelectedOptions: []
    };
  },
  computed: {
    canSelectOptions() {
      return this.optionList.filter((item) => {
        return !this.currentSelectedOptions.some(option => option.id === item.id);
      });
    },
    watchCurrentSelectedOptions() {
      return JSON.parse(JSON.stringify(this.currentSelectedOptions));
    }
  },
  watch: {
    watchCurrentSelectedOptions: {
      handler(nv, ov) {
        if (this.beforeChange && !this.updating) {
          // Process data updates outside the component.
          this.beforeChange(nv, ov, (result) => {
            this.$emit('change', result.map(({ id, number }) => ({ id, number })))
            this.$nextTick(() => {
              this.initData();
            })
          });
        } else {
          this.$emit('change', nv.map((item) => (item)));
        }
      },
      deep: true,
    },
  },
  methods: {
    initData() {
      this.updating = true;
      this.currentSelectedOptions = this.selectedOptions.map((item) => {
        const option = this.optionList.find(option => option.id === item.id);
        return {
          ...option,
          ...item,
        };
      });
      this.$nextTick(() => {
        this.updating = false;
      });
    },
    getOptionList(activeId) {
      const currentActiveOption = this.currentSelectedOptions.find(o => o.id === activeId);
      const otherSelectOptions = currentActiveOption?.disableSwitch? [] : this.canSelectOptions;
      return [this.optionList.find(option => option.id === activeId), ...otherSelectOptions].filter(option => Boolean(option))
    },
    hasNoDesc(options) {
      return options.every(x => !x.desc);
    },
    getFilterObj(keyValue) {
      if (this.optionList.length === 0) {
        return {id: '', name: '', desc: '', tooltip: ''}
      }
      const foundOption = this.optionList.find(option => option.id === keyValue);
      if (!foundOption) {
        return {id: '', name: '', desc: '', tooltip: ''}
      }
      return foundOption;
    },
    getFilterLabel(currentLabel, value) {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      return this.getFilterObj(value).desc;
    },
    getSelectLabel(item) {
      return `${this.title} ${item.name}`;
    },
    getRemoveLabel(item) {
      const {name, desc} = this.getFilterObj(item.id);
      return `Remove ${this.title} ${name} ${desc}`
    },
    getLearnMoreLabel(item) {
      const {name} = this.getFilterObj(item.id);
      return `Learn more about ${name}`
    },
    removeItem(item) {
      this.currentSelectedOptions = this.currentSelectedOptions.filter(option => option.id !== item.id);
      if (this.dataTracking) {
        this.$tracking.buyInteraction(this.dataTracking?.buyInteraction || '',
            `${this.dataTracking?.buySelection || ''} ${item?.name || ''} ${item?.desc || ''} - remove`);
      }
    },
    newItem() {
      this.canSelectOptions.length && this.currentSelectedOptions.push({
        ...this.canSelectOptions[0],
        number: 1,
      });
    },
    onSelectionChange(id) {
      if (this.dataTracking) {
        let name = this.optionList.find(item => item.id === id)?.name || '';
        let desc = this.optionList.find(item => item.id === id)?.desc || '';
        this.$tracking.buyInteraction(this.dataTracking?.buyInteraction || '',
            `${this.dataTracking?.buySelection || ''} ${name} ${desc} - select`);
      }
      /** *
       * ZOOM-679898
       * Since the zm-select component changes with the change of the element ID,
       * the previous element cannot be focused,
       * so it is necessary to manually focus on the new zm-select component.
       */
      this.$nextTick(() => {
        const targetRef = this.$refs[`select-${id}`];
        const refEle = targetRef?.[0]?.$el;
        if (refEle) {
          const innerEle = refEle.querySelector('.zm-select-span__inner');
          innerEle && innerEle.focus();
        }
      });
    },
  },
  created() {
    this.initData()
  },
}
</script>
<style lang="scss">
@import '@/styles/scss-utils.scss';

.select-number-list {
  &__wrap {
    margin: 0;

    &--item {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;

      .zm-input {
        &.is-disabled {
          .zm-input__inner {
            border-color: #b8b8c4;

            &:hover {
              border-color: #b8b8c4;
            }
          }
        }
      }

      & .zm-select {
        border-color: #B8B8C4;
      }

      &__divider {
        width: 100%;
        height: 1px;
        background-color: #DFE3E8;
        margin: 12px 0;
      }

      &__right {
        display: flex;
        align-items: center;
      }

      &__select {
        width: 265px;

        &--simple {
          width: 200px;

          .zm-select-span__inner {
            min-width: 0px !important;
            font-size: 0 !important;
            visibility: visible !important;
          }
        }

        & .zm-select-prefix {
          font-size: 18px;
          font-weight: 700;
          color: #0B5CFF;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        &.is-disabled .zm-select-prefix {
          color: #6e7680;
        }

        & .zm-select-span__inner {
          font-size: 14px;
          font-weight: 500;
          color: #666487;
        }

        & .zm-select__caret {
          color: #0B5CFF !important;
          font-size: 16px !important;
        }

      }

      &__tooltip {
        margin-left: -3px;

        & .zm-icon-info-outline {
          color: #0B5CFF;
          font-size: 14px;
        }

        &:hover {
          background: none !important;
        }
      }

      &__number {
        font-weight: 500;
        color: #00053D;
        margin-left: 8px;
      }

      &__close {
        width: 20px;
        margin-left: 2px;
        color: #6E7680 !important;

        &:not(.is-disabled):hover {
          color: #e8173d !important;
          background: none !important;
        }
      }
    }
  }

  &__label {
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    padding-left: 8px;
    line-height: 19px;
    color: #00053D;
    @include darkTextColor;
  }
}

.select-number-list-dropdown {
  .zm-select-dropdown__item {
    &.selected {
      background-color: rgba(11, 92, 255, 0.1);
    }

    &:active {
      background-color: rgba(11, 92, 255, 0.1);
    }
  }

  .zm-select-dropdown__item--content {
    display: flex;
    align-items: center;
    line-height: 24px;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    color: #0B5CFF;
    white-space: nowrap;
  }

  &__desc {
    font-size: 14px;
    font-weight: 500;
    color: #666487;
    margin-left: 6px;
  }
}
</style>
